/**
 * Lifted promo analytics helpers for WordPress surfaces.
 * Mirrors dutchie-ecomm-pro/src/analytics promo session + dual-push contract
 * so marketing-site and Dutchie storefront share last-touch attribution.
 */

export const PROMO_SESSION_KEY = 'lifted_promo_attribution';
export const PROMO_TTL_MS = 30 * 60 * 1000;
export const WP_HERO_BAND_CREATIVE_SLOT = 'wp_hero_band';

const compact = (event) =>
	Object.fromEntries(
		Object.entries(event).filter(([, value]) => value !== undefined && value !== null && value !== '')
	);

const pagePath = () => `${window.location.pathname}${window.location.search}`;

const getDataLayer = () => {
	window.dataLayer = window.dataLayer || [];
	return window.dataLayer;
};

const storage = () => {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
};

const toContext = (promo, savedAt = Date.now()) => ({
	promotionId: String(promo.promotionId),
	promotionName: promo.promotionName,
	creativeName: promo.creativeName || undefined,
	creativeSlot: promo.creativeSlot || WP_HERO_BAND_CREATIVE_SLOT,
	savedAt,
});

export const toLiftedPromoAttribution = (promo) => {
	const attribution = {
		promotion_id: promo.promotionId,
		promotion_name: promo.promotionName,
		creative_slot: promo.creativeSlot || WP_HERO_BAND_CREATIVE_SLOT,
	};

	if (promo.creativeName) {
		attribution.creative_name = promo.creativeName;
	}

	return attribution;
};

const fromStored = (raw) => {
	if (!raw || typeof raw !== 'object') {
		return null;
	}

	const promotionId = raw.promotionId || raw.promotion_id;
	const promotionName = raw.promotionName || raw.promotion_name;

	if (!promotionId || !promotionName) {
		return null;
	}

	return {
		promotionId: String(promotionId),
		promotionName,
		creativeName: raw.creativeName || raw.creative_name || undefined,
		creativeSlot: raw.creativeSlot || raw.creative_slot || WP_HERO_BAND_CREATIVE_SLOT,
		savedAt: typeof raw.savedAt === 'number' ? raw.savedAt : 0,
	};
};

export const rememberPromotion = (promo) => {
	const context = toContext(promo);
	const store = storage();

	if (!store) {
		return context;
	}

	try {
		store.setItem(PROMO_SESSION_KEY, JSON.stringify(context));
	} catch {
		// Private mode / quota — still return in-memory context.
	}

	return context;
};

export const readPromotion = (now = Date.now()) => {
	const store = storage();

	if (!store) {
		return null;
	}

	try {
		const raw = store.getItem(PROMO_SESSION_KEY);

		if (!raw) {
			return null;
		}

		const context = fromStored(JSON.parse(raw));

		if (!context) {
			store.removeItem(PROMO_SESSION_KEY);
			return null;
		}

		if (!context.savedAt || now - context.savedAt > PROMO_TTL_MS) {
			store.removeItem(PROMO_SESSION_KEY);
			return null;
		}

		return context;
	} catch {
		return null;
	}
};

export const clearPromotion = () => {
	storage()?.removeItem(PROMO_SESSION_KEY);
};

export const pushLiftedEvent = (event, component, action, payload = {}) => {
	getDataLayer().push(
		compact({
			event,
			lifted_page_path: pagePath(),
			lifted_component: component,
			lifted_action: action,
			...payload,
		})
	);
};

export const pushGa4EcommerceEvent = (eventName, ecommerce) => {
	const dataLayer = getDataLayer();
	dataLayer.push({ ecommerce: null });
	dataLayer.push(
		compact({
			event: eventName,
			ecommerce,
		})
	);
};

export const publishPromoContext = () => {
	const promo = readPromotion();
	getDataLayer().push({
		lifted_promo_attribution: promo ? toLiftedPromoAttribution(promo) : null,
	});
};

const toGa4PromotionItem = (promo) =>
	compact({
		promotion_id: String(promo.promotionId),
		promotion_name: promo.promotionName,
		creative_name: promo.creativeName,
		creative_slot: promo.creativeSlot || WP_HERO_BAND_CREATIVE_SLOT,
	});

const liftedPromotionFields = (promo) =>
	compact({
		lifted_promotion_id: String(promo.promotionId),
		lifted_promotion_name: promo.promotionName,
		lifted_creative_name: promo.creativeName,
		lifted_creative_slot: promo.creativeSlot || WP_HERO_BAND_CREATIVE_SLOT,
	});

/** Dual-push view_promotion (impression only — does not write session). */
export const trackViewPromotion = (promo, component = 'wp_hero_band') => {
	if (!promo?.promotionId || !promo?.promotionName) {
		return;
	}

	const item = toGa4PromotionItem(promo);
	pushLiftedEvent(
		'lifted_ecommerce_view_promotion',
		component,
		'view_promotion',
		liftedPromotionFields(promo)
	);
	pushGa4EcommerceEvent('view_promotion', { items: [item] });
};

/** Dual-push select_promotion and remember last-touch for purchase merge. */
export const trackSelectPromotion = (promo, component = 'wp_hero_band') => {
	if (!promo?.promotionId || !promo?.promotionName) {
		return;
	}

	rememberPromotion(promo);
	publishPromoContext();

	const item = toGa4PromotionItem(promo);
	pushLiftedEvent(
		'lifted_ecommerce_select_promotion',
		component,
		'select_promotion',
		liftedPromotionFields(promo)
	);
	pushGa4EcommerceEvent('select_promotion', { items: [item] });
};
